import { LeadTimeFilterOption, MovFilterOption } from "../types/pricelist.types";

export const SUPPLY_TYPE = {
    PRE_ORDER: "pre-order",
    STOCK: "stock",
} as const;

export const PRICELIST_TEXT = {
    PAGE_TITLE_PRE_ORDER: "Pre-Order Opportunities",
    PAGE_TITLE_STOCK: "Stock Pricelists",
    HOME: "Home",
    UNLOCK_WHOLESALE_OFFERS: "Unlock wholesale offers",
    SIGN_IN: "Sign in",
    UPDATED_DAILY: "Updated daily",
    BRANDS_LABEL: "Brands",
    ACTIVE_PRE_ORDERS_LABEL: "Active pre-orders",
    FILTER_BRAND: "Brand",
    FILTER_MOV: "MOV",
    FILTER_ETA: "ETA",
    FILTER_DEPOSIT: "Deposit",
    ALL_BRANDS: "All brands",
    SEARCH_BRANDS: "Search brands…",
    ANY: "Any",
    CLEAR_ALL: "Clear all",
    APPLY_FILTERS: "Apply filters",
    ACTIVE_FILTERS: "Active filters",
    COL_BRAND: "Brand",
    COL_MOV: "MOV (from)",
    COL_ETA: "ETA",
    COL_OFFER_ID: "Offer ID",
    COL_DEPOSIT: "Deposit",
    COL_ACTION: "Action",
    VIEW_OFFERS: "View offers",
    HIDE_OFFERS: "Hide offers",
    VIEW_PRODUCTS: "View products",
    DOWNLOAD: "Download",
    FILTER_HINT:
        "Expand each brand to compare MOQ, ETA and product availability.",
    FILTER_HINT_LIMIT: "We show 1–5 top pre-order offers per brand, for easier comparison.",
    OFFER_FOOTNOTE:
        "Final quantities subject to supplier confirmation. Full boxes only. Payments in EUR.",
    BRAND_CHECK_TITLE: "Can't find the brand or quantity you need?",
    BRAND_CHECK_BODY:
        "Tell us the brand name, EAN list, or target quantity. We'll check availability within 1–2 business days and get back to you.",
    BRAND_CHECK_CTA: "Request brand check",
    BRAND_CHECK_TIME: "Respond in 1–2 business days",
    BRAND_CHECK_NETWORK: "Verified supplier network",
    BRAND_CHECK_MANAGER: "Personal account manager",
    SHOWING: "Showing",
    TO: "to",
    BRANDS_WORD: "brands",
    OFFERS_WORD: "offers",
    MATCH_SUFFIX: "brands match",
    DOWNLOAD_CATALOG: "Download Catalog",
    DOWNLOAD_PREORDER_BRANDLISTS: "Download Pre-order brandlists",
    SHOW_LABEL: "Show:",
    PER_PAGE: "per page",
    PAGE_OF: "Page",
    OF: "of",
    TOTAL_RESULTS: "total results",
    FIRST: "First",
    PREVIOUS: "Previous",
    NEXT: "Next",
    LAST: "Last",
    NO_RESULTS: "No pricelists found.",
    NOT_FOUND_EANS: "Not Found Products EANs:",
    MOQ_NOT_MET:
        "Products with the following EAN did not meet the minimum order quantity:",
    MOV_TOOLTIP: "Minimum Order Value",
    DOWNLOAD_SUCCESS: "Supplier catalog downloaded successfully",
    DOWNLOAD_ERROR_PREFIX: "Error: ",
} as const;

export const PRICELIST_CARD_TEXT = {
    PRE_ORDER_TITLE: "Pre-Order Pricelist",
    STOCK_TITLE: "Stock Pricelist",
    BRAND_LABEL: "Brand:",
    MOV_LABEL: "Minimum order value (MOV):",
    PRE_PAYMENT_LABEL: "Pre-payment",
    FULL_BOXES_LABEL: "Full boxes only",
    HIGHLIGHTS_LABEL: "Highlights:",
    VOLUME_DISCOUNTS_LABEL: "Volume discounts:",
    VIEW_BUTTON: "View",
    DOWNLOAD_LINK: "Download Pricelist",
} as const;

export const MOV_FILTER_IDS = {
    MOV_500: "mov500",
    MOV_1500: "mov1500",
    MOV_5000: "mov5000",
    MOV_10000: "mov10000",
    MOV_15000: "mov15000",
} as const;

export const LEAD_TIME_FILTER_IDS = {
    UP_TO_TWO_WEEKS: "upToTwoWeeks",
    UP_TO_FOUR_WEEKS: "upToFourWeeks",
    UP_TO_SIX_WEEKS: "upToSixWeeks",
    MORE_THAN_SIX_WEEKS: "moreThanSixWeeks",
    UP_TO_2_WEEKS: "up-to-2",
    UP_TO_3_WEEKS: "up-to-3",
    UP_TO_4_WEEKS: "up-to-4",
    UP_TO_5_WEEKS: "up-to-5",
    UP_TO_6_WEEKS: "up-to-6",
    UP_TO_7_WEEKS: "up-to-7",
    UP_TO_8_WEEKS: "up-to-8",
    UP_TO_9_WEEKS: "up-to-9",
    UP_TO_10_WEEKS: "up-to-10",
} as const;

export const MOV_FILTER_OPTIONS: readonly MovFilterOption[] = [
    { id: MOV_FILTER_IDS.MOV_500, label: "500 €", min: 0, max: 50000 },
    { id: MOV_FILTER_IDS.MOV_1500, label: "1500 €", min: 0, max: 150000 },
    { id: MOV_FILTER_IDS.MOV_5000, label: "5000 €", min: 0, max: 500000 },
    { id: MOV_FILTER_IDS.MOV_10000, label: "10000 €", min: 0, max: 1000000 },
    { id: MOV_FILTER_IDS.MOV_15000, label: "15000 €", min: 0, max: 1500000 },
] as const;

export const MOV_LIMIT_SELECT_OPTIONS = [
    { value: "500", label: "Up to 500 €" },
    { value: "1500", label: "Up to 1500 €" },
    { value: "5000", label: "Up to 5000 €" },
    { value: "10000", label: "Up to 10000 €" },
    { value: "15000", label: "Up to 15000 €" },
];

export const LEAD_TIME_FILTER_OPTIONS: readonly LeadTimeFilterOption[] = [
    { id: LEAD_TIME_FILTER_IDS.UP_TO_2_WEEKS, label: "2 weeks", max: 2 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_3_WEEKS, label: "3 weeks", max: 3 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_4_WEEKS, label: "4 weeks", max: 4 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_5_WEEKS, label: "5 weeks", max: 5 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_6_WEEKS, label: "6 weeks", max: 6 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_7_WEEKS, label: "7 weeks", max: 7 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_8_WEEKS, label: "8 weeks", max: 8 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_9_WEEKS, label: "9 weeks", max: 9 },
    { id: LEAD_TIME_FILTER_IDS.UP_TO_10_WEEKS, label: "10 weeks", max: 10 },
] as const;

export const ITEMS_PER_PAGE_OPTIONS = [5, 10, 25, 50, 100] as const;

export const DEFAULT_ITEMS_PER_PAGE = 10;

export const DEFAULT_PREORDER_ITEMS_PER_PAGE = 50;

export const MAX_VISIBLE_PAGES = 5;
